/*
 *
 * File: HeaderProtectedArea.jsx
 *
 * Purpose: Passenger protected area header component.
 *
 * Author:   Thomas Burgard (Software development)
 * Created:  20.04.2023
 * Modified: 20.04.2023
 *
 * Copyright (c) DrivePartner GmbH 2022
 *
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { Menubar } from "primereact/menubar";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { removeUserFromLocalStorage } from "../../utils/localStorage";
import { clearUserDataInState } from "../../store/actions";
import { changeLanguage } from "../../store/actions";

//--------------------------------------------------------------
//--- Variable declarations ------------------------------------
//--------------------------------------------------------------
const USER_LOGOUT = "userLogout";
let username = null;
//--- End component variable declarations ----------------------

/**
 * Implementation of Multistop page header (with menu bar) component.
 *
 * @param {*} props user data
 * @returns JSX-HTML-DOM
 */
const HeaderProtectedArea = (props) => {
  const userData = props.userData;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("common");
  const dispatch = useDispatch();
  const whiteLabel = props.userData.regData.WHITELABEL;
  let items;

  username = props.userData.regData.BenutzerName;

  //--------------------------------------------------------------
  // --- MenuBar items array
  //--------------------------------------------------------------
  if (whiteLabel === "1") {
    items = [
      {
        label: t("navbar.nav-language"),
        items: [
          {
            label: t("navbar.nav-de"),
            command: () => {
              i18n.changeLanguage("de");
              dispatch(changeLanguage({ language: "de" }));
            },
          },
          {
            label: t("navbar.nav-en"),
            command: () => {
              i18n.changeLanguage("en");
              dispatch(changeLanguage({ language: "en" }));
            },
          },
        ],
      },
      {
        label: t("navbar.nav-logout"),
        icon: "pi pi-fw pi-sign-out",
        command: () => {
          routeChange(USER_LOGOUT);
        },
      },
    ];
  } else {
    items = [
      {
        label: t("navbar.nav-language"),
        items: [
          {
            label: t("navbar.nav-de"),
            command: () => {
              i18n.changeLanguage("de");
              dispatch(changeLanguage({ language: "de" }));
            },
          },
          {
            label: t("navbar.nav-en"),
            command: () => {
              i18n.changeLanguage("en");
              dispatch(changeLanguage({ language: "en" }));
            },
          },
        ],
      },
      {
        label: t("navbar.nav-logout"),
        icon: "pi pi-fw pi-sign-out",
        command: () => {
          routeChange(USER_LOGOUT);
        },
      },
    ];
  }

  /**
   * Change the route with given route action code.
   * 
   * @param {*} route route action code
   */
  const routeChange = (route) => {
    if (route === USER_LOGOUT) {
      removeUserFromLocalStorage();
      dispatch(clearUserDataInState());
      navigate("/");
    } else {
      navigate(route);
    }
  };

  /**
   * Set header logo, dependent on username.
   *
   * @returns JSX-Code
   */
  const setHeaderLogo = () => {
    let start = null;

    switch (username) {
      case "prestigechauffeurs":
        start = (
          <a href="https://www.prestigechauffeurs.de/" target="_blank">
            <img
              alt="Prestige Chauffeurs"
              title="Prestige Chauffeurs"
              src="img/logo_prestigechauffeurs.jpg"
              width="160"
              className="mr-3"
            ></img>
          </a>
        );
        break;
      case "royal-transfer":
        start = (
          <a href="https://www.royal-transfer.de/" target="_blank">
            <img
              alt="Royal-Transfer"
              title="Royal-Transfer"
              src="img/logo_royaltransfer_1161.jpg"
              width="160"
              className="mr-3"
            ></img>
          </a>
        );
        break;
      case "cs4you":
        start = (
          <a href="https://www.chauffeurservice4you.de/" target="_blank">
            <img
              alt="Chauffeurservice4you"
              title="Chauffeurservice4you"
              src="img/logo_cs4you_500.jpg"
              width="160"
              className="mr-3"
            ></img>
          </a>
        );
        break;
      case "moudy":
        start = (
          <img
            alt="Moudy"
            title="Moudy"
            src="img/logo_moudy_700.jpg"
            width="160"
            className="mr-3"
          ></img>
        );
        break;
      case "chauffeur-ffm":
        start = (
          <a href="https://chauffeur-ffm.de/" target="_blank">
            <img
              alt="Chauffeur-Ffm"
              title="Chauffeur-Ffm"
              src="img/logo_chauffeur-ffm.jpg"
              width="160"
              className="mr-3"
            ></img>
          </a>
        );
        break;
      case "bavarios":
        start = (
          <a href="https://www.bavarios.de" target="_blank">
            <img
              alt="Bavarios"
              title="Bavarios"
              src="img/logo_bavarios.png"
              width="160"
              className="mr-3"
            ></img>
          </a>
        );
        break;
      case "shekireisen":
        start = (
          <a href="https://www.shekireisen.de/" target="_blank">
            <img
              alt="Shekireisen"
              title="Shekireisen"
              src="img/logo_shekireisen.png"
              width="100"
              className="mr-3"
            ></img>
          </a>
        );
        break;
      case "Vipkurier":
        start = (
          <a href="https://www.vipkurier.eu/" target="_blank">
            <img
              alt="Vipkurier"
              title="Vipkurier"
              src="img/logo_vipkurier.jpg"
              width="100"
              className="mr-3"
            ></img>
          </a>
        );
        break;
      case "taxi-blacksmith":
        start = (
          <a href="https://taxi-blacksmith.de" target="_blank">
            <img
              alt="Taxi-Blacksmith"
              title="Taxi-Blacksmith"
              src="img/logo_blacksmith.jpg"
              width="100"
              className="mr-3"
            ></img>
          </a>
        );
        break;
      case "muster":
        start = (
          <a
            href="https://www.muster.drivepartner.com/home/muster/"
            target="_blank"
          >
            <img
              alt="logo"
              src="img/logo_mustermann.jpg"
              width="160"
              className="mr-3"
            ></img>
          </a>
        );
        break;
      default:
        start = (
          <a href="https://www.drivepartner.com/" target="_blank">
            <img
              alt="logo"
              src="img/logo_drivepartner_190.png"
              width="140"
              className="mr-3"
            ></img>
          </a>
        );
        break;
    }

    if (whiteLabel === "1") start = null;

    return start;
  };

  //*************************************************************/
  //* Returns JSX code                                          */
  //*************************************************************/
  return (
    <>
      <Menubar model={items} start={setHeaderLogo()} />
    </>
  );
};

export default HeaderProtectedArea;
